/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// mantine
import {
	ActionIcon,
	Badge,
	Box,
	Paper,
} from '@mantine/core';

// styles
import classes from './brands.module.css';

// components

// i18n
import { useTranslation } from 'react-i18next';

// icons

// api
import { useBrandsApi } from '@features/resources/brands/api/get-brands';
import { useRemove } from '@features/resources/brands/api/remove-brand';

// configs

// utils
import { useBrands } from '@features/resources/brands/hooks/useBrands';

import { useOrganizationsHasBrands } from '@api/get-organizations-has-entity-brands';
import { Button } from '@components/v2/ui';
import { Datatable } from '@components/v2/ui/Datatable/Datatable';
import { Pagination } from '@components/v2/ui/Pagination';
import { ALL_COUNTRIES } from '@config/constants/all-countries';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import { useDisclosure } from '@mantine/hooks';
import {
	IconFilter,
	IconPlus,
} from '@tabler/icons-react';
import { isEmpty } from 'lodash';
import isNil from 'lodash/isNil';
import { useCountryCodes } from 'shared/api/get-country-codes';

// configs
import { tableSchemaBase as columns } from '@features/resources/brands/brands.columns';
import { tableSchemaBase as columnsForPartner } from '@features/resources/brands/brandsForPartner.columns';
import { tableSchemaBase as columnsWithoutContact } from '@features/resources/brands/brandsWithoutContact.columns';

import { ACLS } from '@config/constants/acls';
import { Details } from '@features/resources/brands/components/Details';
import { Filter } from '@features/resources/brands/components/Filter';
import { TTargetColumn } from '@global-types/index';
import useStore from '@store/index';
import { checkAclFront } from '@utils/acl-front';
import { getVisibleColumns } from '@utils/functions';
import { useState } from 'react';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Brands = () => {
	const { t } = useTranslation();

	const {
		openDrawerEditBrand,
		showScrollFilters,
		openDrawerAddBrand,
		handleAddItem,
		ref,
		brandsDatatableOptions,
		setBrandsDatatableOptions,
		containerRef,
		handleScroll,
		handleOrganizationFilter,
		openDrawer,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		handleOpenConfirmationDeleteItem,
		handleViewMore,
		handleRowClicked,
		handleSelectedRecordsChange,
		handleSortStatusChange,
		isOverflowFilters,
		handlepaginationchangesize,
		handlechangepagination,
		brandsPage,
		brandsPageSize,
		brandsOpenConfirmationModalDelete,
		setBrandsOpenConfirmationModalDelete,
		brandsQueryParams,
		brandsSortStatus,
		brandsSortParams,
		brandsSelectedRow,
		handleCountryFilter,
		handleapplystatusfilter,
		handleapplyipfsstatusfilter,
		handleCloseAddBrandDrawer,
		handleCloseEditBrandDrawer,
		handleCloseShowMoreBrandDrawer,
		handleEditBrandDrawer,
		handleEdit,
		setBrandsWithoutContactDatatableOptions,
		brandsWithoutContactDatatableOptions,
		BrandsQueryParams,
		setBrandsQueryParams,
		setBrandsSelectedRecords,
		setBrandsSelectedRow,
		setBrandsWithoutOrganizationDatatableOptions,
		brandsWithoutOrganizationDatatableOptions,
	} = useBrands();

	const {
		brandsForPartnerDatatableOptions,
		brandsForProducerDatatableOptions,
		setBrandsForPartnerDatatableOptions,
	} = useStore();

	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);

	// APIs
	const { data: organizations } =
		useOrganizationsHasBrands();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useBrandsApi(
		brandsPage,
		brandsPageSize,
		brandsQueryParams,
		brandsSortParams
	);

	const remove = useRemove(brandsPage, brandsPageSize);

	const { data: countryCodes } =
		useCountryCodes('brands');

	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? ALL_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const { checkAcls, checkRole, role } =
		useAuthorization();

	const [filtersNbr, setFiltersNbr] = useState(0);

	return (
		<Paper className={classes['main-layout']}>
			<Details
				opened={!isEmpty(brandsSelectedRow) && openDrawer}
				data={brandsSelectedRow}
				onClose={() => {
					handleCloseShowMoreBrandDrawer();
					setBrandsSelectedRow({});
				}}
			/>
			{checkAclFront(
				ACLS.FRONT.BRANDS.FILTER,
				role as ROLES
			) && (
				<Filter
					opened={openedFilters}
					onClose={closeFilters}
					title={t('filters.titles.brands')}
					onApplyFilters={(filters) => {
						setFiltersNbr(Object.keys(filters).length);
					}}
					value={brandsQueryParams}
				/>
			)}
			<Box className={classes['action-bar']}>
				{checkAclFront(
					ACLS.FRONT.BRANDS.FILTER,
					role as ROLES
				) && (
					<div
						style={{
							position: 'relative',
						}}
					>
						{filtersNbr > 0 && (
							<Badge
								style={{
									position: 'absolute',
									zIndex: '3',
									right: '-3px',
									fontFamily: 'InterDisplay-Regular',
								}}
								color="var(--mantine-color-igluRed-9)"
								size="xs"
								circle
							>
								{filtersNbr}
							</Badge>
						)}
						<ActionIcon
							variant="outline"
							size="48px"
							radius="xl"
							color={'var(--mantine-color-igluBorder-9)'}
							data-testid="bell-icon-button"
							onClick={openFilters}
							bg={
								!isEmpty(brandsQueryParams)
									? 'var(--mantine-color-igluPrimary-9)'
									: 'transparent'
							}
						>
							<IconFilter
								color={
									!isEmpty(brandsQueryParams)
										? 'var(--mantine-color-igluTextButton-9)'
										: 'var(--mantine-color-igluPrimary-9)'
								}
								radius="xl"
								size={24}
								stroke={1.5}
							/>
						</ActionIcon>
					</div>
				)}

				{checkAcls([ACLS.BRANDS.CREATE]) && (
					<Button
						disabled
						leftSection={
							<IconPlus
								radius="xl"
								size={24}
								stroke={1.5}
							/>
						}
						variant={'primary'}
					>
						{t('brands.addBrand')}
					</Button>
				)}
			</Box>

			<Box className={classes['container-table']}>
				{Array.isArray(data) && (
					<Datatable
						fetching={isLoading}
						onRowClick={handleRowClicked}
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? setBrandsDatatableOptions(
										getVisibleColumns(columns, cols)
									)
								: checkRole([ROLES.ORGANIZATION_ADMIN])
									? setBrandsForPartnerDatatableOptions(
											getVisibleColumns(
												columnsForPartner,
												cols
											)
										)
									: setBrandsWithoutContactDatatableOptions(
											getVisibleColumns(
												columnsWithoutContact,
												cols
											)
										);
						}}
						allColumns={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? columns
								: checkRole([ROLES.ORGANIZATION_ADMIN])
									? columnsForPartner
									: columnsWithoutContact
						}
						columns={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? brandsDatatableOptions
								: checkRole([ROLES.ORGANIZATION_ADMIN])
									? brandsForPartnerDatatableOptions
									: brandsWithoutContactDatatableOptions
						}
						records={data}
						onSortStatusChange={handleSortStatusChange}
						sortstatus={brandsSortStatus}
					/>
				)}
			</Box>

			<Box className={classes['pagination-bar']}>
				<Pagination
					pageSize={brandsPageSize}
					totalItems={totalItems}
					activePage={brandsPage}
					onPageSizeChange={handlepaginationchangesize}
					onChange={handlechangepagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Paper>
	);
};

export default Brands;
